import { action, computed, makeObservable, observable } from 'mobx';

import {
  AIBotPayload,
  AIBotServer,
  AIEducationFields,
  AIImplType,
  AIModelKind,
  IAIEducationParams,
  orderAIModelKind,
  deprecatedAIModelKinds
} from 'shared/entities/smartbotAI';
import {
  ValidatorResult,
  validateIsEmpty,
  validateTextFieldMaxLength
} from 'shared/entities/validator';
import { IRootStore } from 'shared/entities/store/rootStore';
import { AIIntentKind, AITarget } from 'shared/entities/smartbotAIInfo';
import { createAiEducationParamsKey } from 'shared/entities/localStorage';
import { localStorageHandler } from 'stores/localStorageHandler';

import { FieldModel } from '../form';

const MAX_TEXT_LENGTH = 5000;
export const TEXTS_MAX_LENGTH = 5500;

export class AIEducationParamsModel implements IAIEducationParams {
  readonly name: FieldModel<string>;

  /** Модель AI */
  readonly aiModelKind: FieldModel<AIModelKind>;
  /** Цель AI-бота (Получить телефон/Проконсультировать) */
  readonly target: FieldModel<AITarget>;
  /** Цель использования */
  readonly intentKind: FieldModel<AIIntentKind>;

  readonly testMessagesAvailable: FieldModel<number | null>;
  readonly companyDescription: FieldModel<string>;
  readonly faq: FieldModel;
  readonly limitations: FieldModel;
  readonly intentKindText: FieldModel;
  readonly contextPrompt: FieldModel<string>;
  readonly questionPrompt: FieldModel<string>;
  readonly impl: AIImplType | null;
  readonly showUsedSources: FieldModel<boolean>;

  readonly projectId: string | null;
  readonly botId: string | null;

  private _hasStoredData: boolean;

  constructor({
    name,
    testMessagesAvailable,
    companyDescription,
    faq,
    limitations,
    intentKind,
    intentKindText,
    target,
    aiModelKind,
    contextPrompt,
    questionPrompt,
    showUsedSources,
    impl,
    projectId,
    botId
  }: {
    intentKind: AIIntentKind;
    target: AITarget;
    aiModelKind: AIModelKind;
    testMessagesAvailable: number | null;
    impl: AIImplType | null;
    projectId: string | null;
    botId: string | null;
    showUsedSources: boolean;
  } & AIEducationFields) {
    this.projectId = projectId;
    this.botId = botId;

    this.aiModelKind = new FieldModel<AIModelKind>(aiModelKind);
    this.target = new FieldModel<AITarget>(target, [validateIsEmpty]);
    this.intentKind = new FieldModel<AIIntentKind>(intentKind, [
      validateIsEmpty
    ]);
    this.impl = impl;
    this.testMessagesAvailable = new FieldModel<number | null>(
      testMessagesAvailable
    );
    this.showUsedSources = new FieldModel<boolean>(showUsedSources);
    this.aiModelKind = new FieldModel<AIModelKind>(aiModelKind);

    /** Данные сохраненные в localStorage */
    const storedData = AIEducationParamsModel.getStoredData({
      projectId,
      botId
    });

    this._hasStoredData = Boolean(storedData);

    this.name = new FieldModel(storedData?.name ?? name, [validateIsEmpty]);
    this.faq = new FieldModel<string>(storedData?.faq ?? faq, [
      (value) => validateTextFieldMaxLength(value, MAX_TEXT_LENGTH)
    ]);
    this.limitations = new FieldModel<string>(
      storedData?.limitations ?? limitations,
      [(value) => validateTextFieldMaxLength(value, MAX_TEXT_LENGTH)]
    );
    this.companyDescription = new FieldModel<string>(
      storedData?.companyDescription ?? companyDescription,
      [
        validateIsEmpty,
        (value) => validateTextFieldMaxLength(value, MAX_TEXT_LENGTH)
      ]
    );
    this.intentKindText = new FieldModel<string>(
      storedData?.intentKindText ?? intentKindText,
      [validateIsEmpty]
    );
    this.contextPrompt = new FieldModel<string>(
      storedData?.contextPrompt ?? contextPrompt,
      [validateTextFieldMaxLength]
    );
    this.questionPrompt = new FieldModel<string>(
      storedData?.questionPrompt ?? questionPrompt,
      [validateTextFieldMaxLength]
    );

    makeObservable<
      AIEducationParamsModel,
      '_hasStoredData' | 'setHasUnsavedData'
    >(this, {
      _hasStoredData: observable,

      isError: computed,
      isTouched: computed,
      companyDescriptionError: computed,
      limitationsError: computed,
      faqError: computed,
      numberOfExtraCharacters: computed,
      textsMaxLengthError: computed,
      hasUnsavedData: computed,

      setHasUnsavedData: action
    });
  }

  get hasUnsavedData(): boolean {
    return this._hasStoredData;
  }

  get isTouched(): boolean {
    if (this.target.value === AITarget.consult) {
      return true;
    }

    if (this.hasUnsavedData) {
      return true;
    }

    return (
      this.name.touched ||
      this.aiModelKind.touched ||
      this.limitations.touched ||
      this.faq.touched ||
      this.companyDescription.touched ||
      this.intentKind.touched ||
      this.intentKindText.touched ||
      this.target.touched
    );
  }

  get isError(): boolean {
    if (this.name.isError) {
      return true;
    }

    if (this.target.value === AITarget.consult) {
      return this.contextPrompt.isError || this.questionPrompt.isError;
    }

    if (
      this.textsMaxLengthError ||
      this.companyDescription.isError ||
      this.faqError ||
      this.limitationsError
    ) {
      return true;
    }

    return this.intentKind.value === AIIntentKind.other
      ? this.intentKindText.isError
      : this.intentKind.isError;
  }

  get numberOfExtraCharacters(): number {
    return (
      this.companyDescription.value.length +
      this.faq.value.length +
      this.limitations.value.length -
      TEXTS_MAX_LENGTH
    );
  }

  get textsMaxLengthError(): ValidatorResult {
    const error = (t) =>
      t('education.left.errors.textsMaxLength', { ns: 'smartbotAIPage' });
    if (this.numberOfExtraCharacters > 0) {
      return error;
    } else {
      return null;
    }
  }

  get faqError(): ValidatorResult {
    return this.textsMaxLengthError || this.faq.error;
  }

  get companyDescriptionError(): ValidatorResult {
    return this.textsMaxLengthError || this.companyDescription.error;
  }

  get limitationsError(): ValidatorResult {
    return this.textsMaxLengthError || this.limitations.error;
  }

  get supportedAIModels(): AIModelKind[] {
    const filtered = orderAIModelKind.filter(
      (model) => !deprecatedAIModelKinds.has(model)
    );

    const currentModel = this.aiModelKind.value;
    const initialModel = this.aiModelKind.initialValue;
    const showCurrentModel =
      !filtered.includes(currentModel) &&
      deprecatedAIModelKinds.has(currentModel);
    const showInitialModel =
      currentModel !== initialModel &&
      !filtered.includes(initialModel) &&
      deprecatedAIModelKinds.has(initialModel);

    if (showCurrentModel) {
      filtered.push(currentModel);
    }

    if (showInitialModel) {
      filtered.push(initialModel);
    }

    return filtered;
  }

  get hasShowUsedSourcesField(): boolean {
    return (
      this.target.value === AITarget.consult && this.impl === AIImplType.openAi
    );
  }

  private setHasUnsavedData = (value: boolean) => {
    this._hasStoredData = value;
  };

  validateIntentKind = (): ValidatorResult => {
    if (this.intentKind.value === AIIntentKind.other) {
      return this.intentKindText.validate();
    }

    return this.intentKind.validate();
  };

  onNameFieldBlur = (): void => {
    this.name.validate();

    if (this.name.touched) {
      this.setStoredData();
    }
  };

  onLimitationsFieldBlur = (): void => {
    this.limitations.validate();

    if (this.limitations.touched) {
      this.setStoredData();
    }
  };

  onFaqFieldBlur = (): void => {
    this.faq.validate();

    if (this.faq.touched) {
      this.setStoredData();
    }
  };

  onCompanyDescriptionFieldBlur = (): void => {
    this.companyDescription.validate();

    if (this.companyDescription.touched) {
      this.setStoredData();
    }
  };

  onIntentKindTextBlur = (): void => {
    this.validateIntentKind();

    if (this.intentKindText.touched) {
      this.setStoredData();
    }
  };

  onQuestionPromptFieldBlur = (): void => {
    this.questionPrompt.validate();

    if (this.questionPrompt.touched) {
      this.setStoredData();
    }
  };

  onContextPromptBlur = (): void => {
    this.contextPrompt.validate();

    if (this.contextPrompt.touched) {
      this.setStoredData();
    }
  };

  validate = (): void => {
    if (this.target.value === AITarget.consult) {
      this.contextPrompt.validate();
      this.questionPrompt.validate();
      return;
    }

    this.name.validate();
    this.limitations.validate();
    this.faq.validate();
    this.companyDescription.validate();
    this.validateIntentKind();
    this.intentKind.validate();
  };

  resetTouched = (): void => {
    this.name.resetTouched();
    this.aiModelKind.resetTouched();
    this.faq.resetTouched();
    this.limitations.resetTouched();
    this.companyDescription.resetTouched();
    this.intentKind.resetTouched();
    this.intentKindText.resetTouched();
    this.target.resetTouched();
    this.questionPrompt.resetTouched();
    this.contextPrompt.resetTouched();
  };

  toJson(): AIBotPayload {
    const showUsedSources = this.hasShowUsedSourcesField
      ? this.showUsedSources.value
      : undefined;

    return {
      name: this.name.value,
      target: this.target.value,
      company_description: this.companyDescription.value,
      intent_kind: this.intentKind.value,
      intent_text:
        this.intentKind.value === AIIntentKind.other
          ? this.intentKindText.value
          : undefined,
      faq: this.faq.value || null,
      limitations: this.limitations.value || null,
      model_type: this.aiModelKind.value,
      context_prompt: this.contextPrompt.value,
      question_prompt: this.questionPrompt.value,
      show_used_sources: showUsedSources
    };
  }

  // Метод для очистки данных из localStorage после успешного сохранения на беке
  clearStoredData(botId: string | null): void {
    if (!this.projectId || !botId) {
      return;
    }
    try {
      localStorageHandler.remove(
        createAiEducationParamsKey(this.projectId, botId)
      );
      this.setHasUnsavedData(false);
    } catch {
      // Ignore storage errors
    }
  }

  private setStoredData(): void {
    if (!this.projectId || !this.botId) {
      return;
    }

    try {
      const data: AIEducationFields = {
        name: this.name.value,
        companyDescription: this.companyDescription.value,
        intentKindText: this.intentKindText.value,
        faq: this.faq.value,
        limitations: this.limitations.value,
        contextPrompt: this.contextPrompt.value,
        questionPrompt: this.questionPrompt.value
      };
      localStorageHandler.setItem(
        createAiEducationParamsKey(this.projectId, this.botId),
        JSON.stringify(data)
      );
      this.setHasUnsavedData(true);
      this.resetTouched();
    } catch {
      // Ignore storage errors
    }
  }

  private static getStoredData({
    projectId,
    botId
  }: {
    projectId: string | null;
    botId: string | null;
  }): AIEducationFields | null {
    if (!projectId || !botId) {
      return null;
    }

    try {
      const data = localStorageHandler.getItem(
        createAiEducationParamsKey(projectId, botId)
      );
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  static fromDefaultParams(rootStore: IRootStore): AIEducationParamsModel {
    const length = rootStore.smartbotAIBotsStore.bots.items.filter((bot) =>
      bot.name.startsWith('Smartbot AI')
    ).length;
    const name = !length ? 'Smartbot AI' : `Smartbot AI (${length})`;

    return new AIEducationParamsModel({
      name,
      testMessagesAvailable: null,
      companyDescription: '',
      intentKind:
        rootStore.smartbotAIInfoStore.intentKinds.items[0]?.kind ??
        AIIntentKind.buy,
      intentKindText: '',
      target: AITarget.consult,
      faq: '',
      limitations: rootStore.translationsStore.t(
        'education.left.limitations.defaultValue',
        {
          ns: 'smartbotAIPage'
        }
      ),
      aiModelKind: AIModelKind.gpt4oMini,
      contextPrompt: rootStore.smartbotAIInfoStore.defaultContextPrompt,
      questionPrompt: rootStore.smartbotAIInfoStore.defaultQuestionPrompt,
      impl: AIImplType.openAi,
      projectId: rootStore.projectId,
      botId: null,
      showUsedSources: false
    });
  }

  static fromJson(
    raw: AIBotServer,
    projectId: string | null
  ): AIEducationParamsModel {
    return new AIEducationParamsModel({
      name: raw.name,
      testMessagesAvailable: raw.test_messages_available,
      companyDescription: raw.company_description || '',

      intentKind: raw.intent_kind,
      intentKindText: raw.intent_text || '',
      target: raw.target,

      faq: raw.faq || '',
      limitations: raw.limitations || '',
      aiModelKind: raw.model_type,
      contextPrompt: raw.context_prompt,
      questionPrompt: raw.question_prompt,
      showUsedSources: raw.show_used_sources ?? false,
      impl: raw.impl ?? null,
      botId: raw._id,
      projectId
    });
  }
}
